import styles from './Checkout.module.css';

/**
 * Says how payment will be taken, and does not take it.
 *
 * There is no card field here on purpose. Razorpay Checkout opens as an overlay
 * on its own domain when the shopper presses Pay, and the instrument is typed
 * into that — so neither this app nor its server ever handles a card number,
 * which is most of what makes taking payments safe to do at all.
 *
 * What is left for this section is the honest description of what is about to
 * happen, including whether it is real money. `config` is whatever
 * /api/payments/config said; it is null until the shopper signs in (the route
 * needs a session), and the panel reads sensibly without it.
 */

const METHODS = [
  { icon: '💳', label: 'Cards' },
  { icon: '🏦', label: 'Netbanking' },
  { icon: '📱', label: 'UPI' },
  { icon: '👛', label: 'Wallets' },
];

export function PaymentSection({ step = 2, config, currency }) {
  // Absent config is not "not live" — it is "not known yet". Only an explicit
  // answer moves the panel off its neutral wording.
  const mode = config?.mode ?? null;
  const isLive = config?.is_live ?? false;
  const isMock = mode === 'mock';
  const isTest = mode === 'test';
  const isBroken = mode === 'misconfigured';

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>
        <span className={styles.step}>{step}</span>Payment
      </h2>

      <div className={styles.gateway}>
        <div className={styles.gatewayHead}>
          <span className={styles.gatewayName}>Razorpay</span>
          {mode && !isLive && (
            <span className={`${styles.modeBadge} ${isBroken ? styles.modeBadgeBad : ''}`}>
              {isBroken ? 'Unavailable' : isMock ? 'Simulated' : 'Test mode'}
            </span>
          )}
          {isLive && <span className={styles.secureBadge}>🔒 Secure</span>}
        </div>

        <p className={styles.gatewayText}>
          {isBroken ? (
            <>
              Card payments are temporarily unavailable. Nothing has been charged — please try
              again shortly.
            </>
          ) : (
            <>
              Pressing pay opens Razorpay&apos;s secure checkout
              {currency ? <> to collect {currency}</> : null}. Your card details go straight to
              Razorpay — TUBMarket never sees or stores them.
            </>
          )}
        </p>

        {!isBroken && (
          <ul className={styles.methods}>
            {METHODS.map((m) => (
              <li key={m.label} className={styles.method}>
                <span aria-hidden="true">{m.icon}</span> {m.label}
              </li>
            ))}
          </ul>
        )}

        {/* Sandbox guidance. Shown only when this deployment genuinely cannot
            take real money, so it can never read as an invitation on a live
            store — and the test card is Razorpay's own published domestic one,
            because an international test card is refused by Indian test accounts
            with an error that looks like a bug. */}
        {isTest && (
          <div className={styles.testNote}>
            <strong>Test mode — no money moves.</strong>
            <div className={styles.testGrid}>
              <span>Card</span>
              <code>4718 6091 0820 4366</code>
              <span>CVV / Expiry</span>
              <code>123 · any future date</code>
              <span>OTP</span>
              <code>123456</code>
              <span>or UPI</span>
              <code>success@razorpay</code>
            </div>
          </div>
        )}

        {isMock && (
          <div className={styles.testNote}>
            <strong>Simulated gateway.</strong> No Razorpay keys are configured here, so there is
            no checkout to open — you&apos;ll be asked to approve the payment by hand instead.
          </div>
        )}
      </div>
    </section>
  );
}
